import { useEffect, useState } from 'react';
import { useRange } from 'react-instantsearch';

import {
  toNumberUnlessEmpty,
  parseNumberUnless,
  isRangeMaxSafeInteger,
  isRangeMinSafeInteger,
} from '../../util/rangeNumberConversion';

interface UseSearchRangeSliderProps {
  attribute: string;
}

export const useSearchNumberRange = ({ attribute }: UseSearchRangeSliderProps) => {
  const {
    start: [startMin, startMax],
    refine,
  } = useRange({ min: Number.MIN_SAFE_INTEGER, max: Number.MAX_SAFE_INTEGER, attribute });

  const [min, setMin] = useState<number | undefined>(parseNumberUnless(isRangeMinSafeInteger)(startMin));
  const [max, setMax] = useState<number | undefined>(parseNumberUnless(isRangeMaxSafeInteger)(startMax));

  useEffect(() => {
    if (startMin === Number.NEGATIVE_INFINITY && startMax === Number.POSITIVE_INFINITY) {
      setMin(undefined);
      setMax(undefined);
    }
  }, [startMin, startMax]);

  const handleMinChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newMin = toNumberUnlessEmpty(event.target.value);
    if (isRangeMinSafeInteger(newMin)) {
      setMin(undefined);
      refine([undefined, max]);
      return;
    }

    setMin(newMin); // allow undefined to clear the input field and don't default to 0
    refine([newMin, max]);
  };

  const handleMaxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newMax = toNumberUnlessEmpty(event.target.value);
    if (isRangeMaxSafeInteger(newMax)) {
      setMax(undefined);
      refine([undefined, max]);
      return;
    }
    setMax(newMax); // allow undefined to clear the input field and don't default to 0
    refine([min, newMax]);
  };

  return {
    minInputProps: {
      value: min ?? '',
      onChange: handleMinChange,
    },
    maxInputProps: {
      value: max ?? '',
      onChange: handleMaxChange,
    },
  };
};
